import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Eye, CheckCircle2, XCircle, Clock, Sparkles, MessageCircle,
  Send, Package, IndianRupee, Mail, Phone, Briefcase, ExternalLink
} from "lucide-react";
import { SkeletonList } from "@/components/admin/Skeleton";

const STATUS_META = {
  new:         { label: "New",          color: "text-blue-400 bg-blue-600/10 border-blue-600/20",       Icon: Sparkles },
  in_review:   { label: "In Review",    color: "text-yellow-400 bg-yellow-600/10 border-yellow-600/20", Icon: Eye },
  approved:    { label: "Approved",     color: "text-green-400 bg-green-600/10 border-green-600/20",    Icon: CheckCircle2 },
  in_progress: { label: "In Progress",  color: "text-purple-400 bg-purple-600/10 border-purple-600/20", Icon: Clock },
  completed:   { label: "Completed",    color: "text-emerald-400 bg-emerald-600/10 border-emerald-600/20", Icon: CheckCircle2 },
  rejected:    { label: "Rejected",     color: "text-red-400 bg-red-600/10 border-red-600/20",          Icon: XCircle },
};
const STATUS_ORDER = ["new", "in_review", "approved", "in_progress", "completed", "rejected"];

export default function OrdersPanel({ API, getAuthHeaders }) {
  const [orders, setOrders] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const fetchOrders = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/orders`, { headers: getAuthHeaders() });
      setOrders(data.orders || []);
      setCounts(data.counts || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load orders");
    }
    setLoading(false);
  }, [API, getAuthHeaders]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const updateOrder = async (orderId, updates) => {
    try {
      const { data } = await axios.patch(`${API}/admin/orders/${orderId}`, updates, { headers: getAuthHeaders() });
      setOrders(prev => prev.map(o => o.id === orderId ? data : o));
      if (selected?.id === orderId) setSelected(data);
      toast.success("Order updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const filtered = filter === "all" ? orders : orders.filter(o => o.status === filter);

  return (
    <div className="space-y-6">
      {/* Filter chips */}
      <div className="flex flex-nowrap overflow-x-auto no-scrollbar gap-2 scroll-snap-x">
        <button
          onClick={() => setFilter("all")}
          data-testid="orders-filter-all"
          className={`tap shrink-0 text-[11px] font-bold uppercase tracking-widest border px-3 py-2 transition-colors ${filter === "all" ? "bg-red-600 border-red-600 text-white" : "bg-transparent border-white/10 text-zinc-400 hover:border-white/30 hover:text-white"}`}
        >
          All ({orders.length})
        </button>
        {STATUS_ORDER.map(s => {
          const meta = STATUS_META[s];
          const MetaIcon = meta.Icon;
          const c = counts[s] || 0;
          return (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`orders-filter-${s}`}
              className={`tap shrink-0 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest border px-3 py-2 transition-colors ${filter === s ? `${meta.color} border-current` : "bg-transparent border-white/10 text-zinc-400 hover:border-white/30 hover:text-white"}`}
            >
              <MetaIcon size={11} /> {meta.label} ({c})
            </button>
          );
        })}
      </div>

      {/* List */}
      {loading ? <SkeletonList count={4} /> :
        filtered.length === 0 ? (
          <div className="border border-dashed border-white/10 bg-[#111] p-12 text-center">
            <Package size={32} className="text-zinc-700 mx-auto mb-3" />
            <p className="text-white font-bold uppercase tracking-wider text-sm">No orders{filter !== "all" ? ` in "${STATUS_META[filter].label}"` : " yet"}</p>
            <p className="text-xs text-zinc-500 mt-1">Orders placed by clients will appear here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(o => {
              const meta = STATUS_META[o.status] || STATUS_META.new;
              const MetaIcon = meta.Icon;
              return (
                <button
                  key={o.id}
                  onClick={() => setSelected(o)}
                  data-testid={`admin-order-${o.id}`}
                  className="tap-soft w-full text-left border border-white/5 bg-[#111] hover:border-red-600/40 hover:bg-[#151515] p-4 sm:p-5 transition-all"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest border px-2 py-0.5 ${meta.color}`}>
                        <MetaIcon size={10} /> {meta.label}
                      </span>
                      <span className="text-[10px] text-zinc-600 uppercase tracking-wider">
                        {new Date(o.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                      </span>
                      {o.messages?.length > 0 && (
                        <span className="inline-flex items-center gap-1 text-[10px] text-zinc-500">
                          <MessageCircle size={10} className="text-red-500" /> {o.messages.length}
                        </span>
                      )}
                    </div>
                    {o.quoted_amount != null && (
                      <span className="text-sm font-black text-red-500">Rs {Number(o.quoted_amount).toLocaleString("en-IN")}</span>
                    )}
                  </div>
                  <h3 className="text-sm sm:text-base font-black uppercase text-white mb-1">{o.title}</h3>
                  <p className="text-xs text-zinc-500 mb-2">
                    {o.client_name} · {o.business_name || o.business_type || "—"} · {o.client_email}
                  </p>
                  <p className="text-xs text-zinc-400 line-clamp-2">{o.description}</p>
                </button>
              );
            })}
          </div>
        )
      }

      {selected && (
        <AdminOrderModal
          order={selected}
          onClose={() => setSelected(null)}
          onUpdate={updateOrder}
          API={API}
          getAuthHeaders={getAuthHeaders}
          refresh={fetchOrders}
        />
      )}
    </div>
  );
}

function AdminOrderModal({ order, onClose, onUpdate, API, getAuthHeaders, refresh }) {
  const [note, setNote] = useState(order.admin_note || "");
  const [quote, setQuote] = useState(order.quoted_amount != null ? String(order.quoted_amount) : "");
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [fullOrder, setFullOrder] = useState(order);

  useEffect(() => {
    document.body.classList.add("no-scroll");
    // Fetch full order with latest messages
    axios.get(`${API}/orders/${order.id}`, { headers: getAuthHeaders() })
      .then(r => setFullOrder(r.data))
      .catch(() => {});
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.classList.remove("no-scroll");
      window.removeEventListener("keydown", onKey);
    };
  }, [order.id, API, getAuthHeaders, onClose]);

  const saveMeta = async () => {
    await onUpdate(order.id, {
      admin_note: note,
      quoted_amount: quote ? parseInt(quote) : null,
    });
  };

  const changeStatus = (status) => onUpdate(order.id, { status });

  const sendMsg = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    setSending(true);
    try {
      const { data: newMsg } = await axios.post(`${API}/orders/${order.id}/message`, { message: msg }, { headers: getAuthHeaders() });
      setFullOrder(prev => ({ ...prev, messages: [...(prev.messages || []), newMsg] }));
      setMsg("");
      refresh();
    } catch {
      toast.error("Failed to send");
    }
    setSending(false);
  };

  const meta = STATUS_META[fullOrder.status] || STATUS_META.new;
  const MetaIcon = meta.Icon;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-0 sm:p-4 animate-fadeIn"
      data-testid="admin-order-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-3xl h-[100dvh] sm:h-auto sm:max-h-[90vh] overflow-y-auto relative">
        <div className="sticky top-0 z-10 bg-[#0A0A0A] border-b border-white/10 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest border px-2 py-0.5 shrink-0 ${meta.color}`}>
              <MetaIcon size={10} /> {meta.label}
            </span>
            <h2 className="text-sm sm:text-base font-black uppercase truncate">{fullOrder.title}</h2>
          </div>
          <button onClick={onClose} className="tap touch-target inline-flex items-center justify-center text-zinc-400 hover:text-white" aria-label="Close">
            <XCircle size={22} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Client info */}
          <div className="border border-white/10 bg-[#111] p-4">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Client</p>
            <p className="text-white font-bold mb-1">{fullOrder.client_name} {fullOrder.business_name && <span className="text-zinc-500 font-normal">· {fullOrder.business_name}</span>}</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-400">
              <a href={`mailto:${fullOrder.client_email}`} className="tap flex items-center gap-1 hover:text-red-500"><Mail size={11} /> {fullOrder.client_email}</a>
              {fullOrder.client_phone && (
                <a href={`https://wa.me/${fullOrder.client_phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="tap flex items-center gap-1 hover:text-green-500"><Phone size={11} /> {fullOrder.client_phone}</a>
              )}
              {fullOrder.business_type && <span className="flex items-center gap-1"><Briefcase size={11} /> {fullOrder.business_type}</span>}
            </div>
          </div>

          {/* Brief */}
          <div>
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Brief</p>
            <p className="text-sm text-zinc-300 whitespace-pre-wrap">{fullOrder.description}</p>
            <div className="mt-3 flex flex-wrap gap-3 text-[11px]">
              {fullOrder.budget != null && <span className="text-zinc-500">Budget: <span className="text-white font-bold">Rs {Number(fullOrder.budget).toLocaleString("en-IN")}</span></span>}
              {fullOrder.timeline && <span className="text-zinc-500">Timeline: <span className="text-white font-bold">{fullOrder.timeline}</span></span>}
            </div>
            {fullOrder.reference_urls && (
              <p className="mt-2 text-xs text-zinc-500 flex items-center gap-1"><ExternalLink size={11} /> {fullOrder.reference_urls}</p>
            )}
          </div>

          {/* Status actions */}
          <div>
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Change Status</p>
            <div className="flex flex-wrap gap-2">
              {STATUS_ORDER.map(s => {
                const sm = STATUS_META[s];
                const SIcon = sm.Icon;
                const active = fullOrder.status === s;
                return (
                  <button
                    key={s}
                    onClick={() => changeStatus(s)}
                    data-testid={`admin-order-status-${s}`}
                    className={`tap inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest border px-3 py-2 transition-colors ${active ? `${sm.color} border-current` : "bg-transparent border-white/10 text-zinc-400 hover:border-white/30 hover:text-white"}`}
                  >
                    <SIcon size={11} /> {sm.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Admin note & quote */}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_200px] gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">Internal / Client Note</label>
              <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
                placeholder="Visible to the client on their dashboard"
                data-testid="admin-order-note"
                className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm text-white focus:border-red-600 outline-none resize-none" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 flex items-center gap-1"><IndianRupee size={11} /> Quoted Amount</label>
              <input value={quote} onChange={e => setQuote(e.target.value)} type="number" inputMode="numeric" min="0"
                placeholder="e.g. 30000"
                data-testid="admin-order-quote"
                className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm text-white focus:border-red-600 outline-none" />
            </div>
          </div>
          <button onClick={saveMeta} data-testid="admin-order-save-meta"
            className="tap bg-white text-black px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-red-600 hover:text-white transition-colors">
            Save Note & Quote
          </button>

          {/* Messages */}
          <div>
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1"><MessageCircle size={11} /> Conversation ({fullOrder.messages?.length || 0})</p>
            <div className="space-y-2 max-h-60 overflow-y-auto pr-1 mb-3">
              {(fullOrder.messages || []).map(m => (
                <div key={m.id} className={`border p-3 ${m.sender === "client" ? "border-blue-600/20 bg-blue-600/5 ml-0 mr-8" : "border-red-600/20 bg-red-600/5 ml-8 mr-0"}`}>
                  <p className="text-[10px] uppercase tracking-widest mb-1 text-zinc-500">{m.sender_name} · {new Date(m.created_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}</p>
                  <p className="text-sm text-white whitespace-pre-wrap">{m.message}</p>
                </div>
              ))}
              {(fullOrder.messages || []).length === 0 && <p className="text-xs text-zinc-600 italic">No messages yet.</p>}
            </div>
            <form onSubmit={sendMsg} className="flex gap-2">
              <input value={msg} onChange={e => setMsg(e.target.value)}
                placeholder="Reply to client..."
                enterKeyHint="send"
                data-testid="admin-order-msg-input"
                className="flex-1 bg-transparent border border-white/10 px-4 py-3 text-sm text-white focus:border-red-600 outline-none" />
              <button type="submit" disabled={sending || !msg.trim()} data-testid="admin-order-msg-send"
                className="tap bg-red-600 text-white px-4 py-3 border-2 border-white disabled:opacity-50 flex items-center justify-center">
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
